import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

interface ReportSidebarSection {
  id: string
  labelKey: string
}

interface ReportSidebarProps {
  sections: ReportSidebarSection[]
  activeId: string
}

/**
 * Sticky scroll-spy table of contents for the desktop report view.
 * Receives the already-filtered section list — no own visibility logic.
 */
export function ReportSidebar({ sections, activeId }: ReportSidebarProps) {
  const { t } = useTranslation()

  const scrollTo = useCallback((id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  return (
    <nav
      aria-label={t('amazonAudit.tableOfContents', { defaultValue: 'Inhaltsverzeichnis' })}
      className="sticky top-16 space-y-0.5 max-h-[calc(100vh-8rem)] overflow-y-auto"
    >
      {sections.map(({ id, labelKey }) => (
        <SidebarLink
          key={id}
          id={id}
          label={t(labelKey, { defaultValue: formatLabel(id) })}
          active={activeId === id}
          onClick={scrollTo}
        />
      ))}
    </nav>
  )
}

function SidebarLink({ id, label, active, onClick }: {
  id: string; label: string; active: boolean; onClick: (id: string) => void
}) {
  return (
    <button
      onClick={() => onClick(id)}
      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
        active
          ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 font-semibold'
          : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 font-medium'
      }`}
    >
      {label}
    </button>
  )
}

function formatLabel(id: string): string {
  return id
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}
